/**
 * Import and resolve motion-timeline media.
 *
 * Import order matters: validate → cache in IndexedDB FIRST (the editor
 * works instantly and offline) → upload to Storage + write the videoAssets
 * doc. Like lib/uploadAsset.ts, a missing sign-in or a Storage failure is not
 * an error: the asset stays IDB-only (`source: 'idb'`) and the timeline
 * shows it as "not backed up yet".
 *
 * Reads always try IndexedDB first; see videoAssetIdb.ts for why.
 */

import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getBlob, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { AudioTrack, VideoClipRef } from '@canvas/shared-types';
import { getClientAuth, getClientDB, getClientStorage } from '@canvas/firebase-config/client';
import { STORAGE_PATHS, STORAGE_RULES } from '@canvas/firebase-config/config';
import { usableDurationMs } from './recordSession';
import { createAudioTrack } from './audioTrack';
import { createClipRef } from './videoClip';
import { deleteVideoAsset, getVideoAsset, putVideoAsset } from './videoAssetIdb';

export interface MediaRef {
  idbKey?: string;
  storagePath?: string;
  url?: string;
}

function assetId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function sanitizeFilename(name: string, fallback = 'audio'): string {
  const cleaned = name.trim().replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return (cleaned || fallback).slice(0, 80);
}

/** Some OSes report MP3s as audio/mp3 or leave the type empty; normalize from the extension. */
function audioMimeOf(file: File): string {
  const type = file.type.toLowerCase().split(';')[0].trim();
  if (type === 'audio/mp3') return 'audio/mpeg';
  if (type === 'audio/x-wav' || type === 'audio/wave') return 'audio/wav';
  if (type === 'audio/x-m4a' || type === 'audio/m4a') return 'audio/mp4';
  if (type) return type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  const byExt: Record<string, string> = {
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    weba: 'audio/webm',
  };
  return (ext && byExt[ext]) || '';
}

/** Duration from metadata, falling back to a full decode (VBR MP3s can report Infinity). */
async function measureAudioMs(blob: Blob): Promise<number> {
  const url = URL.createObjectURL(blob);
  try {
    const fromMeta = await new Promise<number>((resolve) => {
      const el = new Audio();
      el.preload = 'metadata';
      el.onloadedmetadata = () => resolve(el.duration);
      el.onerror = () => resolve(NaN);
      el.src = url;
    });
    if (Number.isFinite(fromMeta) && fromMeta > 0) return Math.round(fromMeta * 1000);
  } finally {
    URL.revokeObjectURL(url);
  }
  const ctx = new AudioContext();
  try {
    const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
    return Math.round(buffer.duration * 1000);
  } finally {
    void ctx.close();
  }
}

/** Import a music file as the timeline's audio track. Throws only for invalid files. */
export async function uploadProjectAudio(options: { file: File; projectId: string; durationHintMs?: number }): Promise<AudioTrack> {
  const { file, projectId, durationHintMs } = options;
  const mimeType = audioMimeOf(file);
  if (!(STORAGE_RULES.ALLOWED_AUDIO_TYPES as readonly string[]).includes(mimeType)) {
    throw new Error('Use an MP3, M4A, AAC, WAV, OGG or WebM audio file.');
  }
  if (file.size > STORAGE_RULES.MAX_AUDIO_FILE_SIZE) {
    throw new Error('Audio files must be 20 MB or smaller.');
  }

  let durationMs = 0;
  try {
    durationMs = await measureAudioMs(file);
  } catch {
    durationMs = 0;
  }
  if (!(durationMs > 0)) durationMs = usableDurationMs(Number.NaN, durationHintMs);
  if (!(durationMs > 0)) throw new Error('That audio file couldn’t be read. Try exporting it as MP3 and import again.');

  const trackId = assetId('aud');
  const name = (file.name || 'Audio').replace(/\.[^.]+$/, '').slice(0, 80);
  const blob = file.type === mimeType ? file : new Blob([file], { type: mimeType });
  await putVideoAsset(trackId, blob);

  const local = createAudioTrack({ trackId, name, source: 'idb', mimeType, durationMs, idbKey: trackId });
  const uid = getClientAuth().currentUser?.uid;
  if (!uid || !projectId) return local;

  try {
    const storagePath = `${STORAGE_PATHS.projectVideoAssets(uid, projectId)}/${trackId}_${sanitizeFilename(file.name)}`;
    const storageRef = ref(getClientStorage(), storagePath);
    await uploadBytes(storageRef, blob, {
      contentType: mimeType,
      customMetadata: { projectId, kind: 'audio', originalName: (file.name || '').slice(0, 120) },
    });
    const url = await getDownloadURL(storageRef);
    await setDoc(doc(getClientDB(), 'users', uid, 'projects', projectId, 'videoAssets', trackId), {
      kind: 'audio',
      name,
      storagePath,
      mimeType,
      sizeBytes: blob.size,
      durationMs,
      createdAt: serverTimestamp(),
    });
    return { ...local, source: 'storage', storagePath, url };
  } catch (err) {
    console.warn('Audio upload failed; keeping the local copy only', err);
    return local;
  }
}

/** Poster long edge. The layer draws it scaled to the clip's natural size until video attaches. */
const POSTER_MAX_SIDE = 1280;
const PROBE_TIMEOUT_MS = 15_000;
/** Frames sampled to measure the source frame rate. */
const FPS_SAMPLE_FRAMES = 12;
const FPS_PROBE_MS = 1500;
/** Common rates; a measured value within 3% snaps to one. */
const COMMON_FPS = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60];

function videoMimeOf(file: File): string {
  const type = file.type.toLowerCase().split(';')[0].trim();
  if (type) return type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  const byExt: Record<string, string> = { mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' };
  return (ext && byExt[ext]) || '';
}

function waitFor(el: HTMLVideoElement, event: string, timeoutMs = PROBE_TIMEOUT_MS): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      el.removeEventListener(event, onOk);
      el.removeEventListener('error', onErr);
      window.clearTimeout(timer);
      resolve(ok);
    };
    const onOk = () => done(true);
    const onErr = () => done(false);
    const timer = window.setTimeout(() => done(false), timeoutMs);
    el.addEventListener(event, onOk);
    el.addEventListener('error', onErr);
  });
}

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
};

/**
 * Browsers don't expose a video's frame rate. Play a few frames muted and
 * take the median gap between presented frames (requestVideoFrameCallback
 * reports each one's media time). Undefined when that API is missing.
 */
async function measureFps(el: FrameCallbackVideo): Promise<number | undefined> {
  if (typeof el.requestVideoFrameCallback !== 'function') return undefined;
  const times: number[] = [];
  const sampled = new Promise<void>((resolve) => {
    const onFrame = (_now: number, meta: { mediaTime: number }) => {
      times.push(meta.mediaTime);
      if (times.length >= FPS_SAMPLE_FRAMES) resolve();
      else el.requestVideoFrameCallback?.(onFrame);
    };
    el.requestVideoFrameCallback?.(onFrame);
    window.setTimeout(resolve, FPS_PROBE_MS);
  });
  try {
    await el.play();
    await sampled;
  } catch {
    return undefined;
  } finally {
    el.pause();
  }
  const gaps = times
    .slice(1)
    .map((t, i) => t - times[i])
    .filter((g) => g > 0.001)
    .sort((a, b) => a - b);
  if (gaps.length < 3) return undefined;
  const measured = 1 / gaps[Math.floor(gaps.length / 2)];
  const snap = COMMON_FPS.find((f) => Math.abs(f - measured) / f < 0.03);
  return snap ?? Math.round(measured);
}

interface VideoProbe {
  durationMs: number;
  width: number;
  height: number;
  fps?: number;
  poster: Blob;
}

/**
 * Duration, size, frame rate and a JPEG poster from the frame at
 * min(100 ms, 10% of the clip). Throws user copy when the browser can't
 * decode the file (HEVC .mov on Chrome is the usual culprit).
 */
async function probeVideo(blob: Blob, durationHintMs?: number): Promise<VideoProbe> {
  const unplayable = 'This video can’t be played in this browser. Try an MP4 (H.264) file.';
  const url = URL.createObjectURL(blob);
  const el = document.createElement('video') as FrameCallbackVideo;
  el.preload = 'auto';
  el.muted = true;
  el.playsInline = true;
  try {
    el.src = url;
    if (!(await waitFor(el, 'loadedmetadata'))) throw new Error(unplayable);
    if (!el.videoWidth || !el.videoHeight) await waitFor(el, 'loadeddata', 1500);
    let durationMs = usableDurationMs(el.duration, durationHintMs);
    if (!(durationMs > 0) || !Number.isFinite(el.duration)) {
      try {
        el.currentTime = 1e101;
      } catch {
        /* An unseekable recording still falls back to the clock hint. */
      }
      await waitFor(el, 'seeked', 1200);
      durationMs = usableDurationMs(el.duration, durationHintMs);
      try {
        el.currentTime = 0;
      } catch {
        /* Poster draw below still uses whatever frame is ready. */
      }
      await waitFor(el, 'seeked', 800);
    }
    if (!(durationMs > 0)) throw new Error('That video appears to be empty.');
    const width = el.videoWidth || 1280;
    const height = el.videoHeight || 720;
    const fps = await measureFps(el);

    el.currentTime = Math.min(100, durationMs * 0.1) / 1000;
    await waitFor(el, 'seeked', 800);
    // 'seeked' can fire before the frame is composited.
    await new Promise<void>((resolve) => {
      if (typeof el.requestVideoFrameCallback === 'function') {
        const timer = window.setTimeout(resolve, 250);
        el.requestVideoFrameCallback(() => {
          window.clearTimeout(timer);
          resolve();
        });
      } else {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      }
    });

    const scale = Math.min(1, POSTER_MAX_SIDE / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error(unplayable);
    ctx.fillStyle = '#111111';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    try {
      if (el.videoWidth) ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
    } catch {
      /* A blank poster still lets the recorded layer land on the timeline. */
    }
    const poster = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));
    if (!poster) throw new Error(unplayable);
    return { durationMs, width, height, fps, poster };
  } finally {
    el.removeAttribute('src');
    el.load();
    URL.revokeObjectURL(url);
  }
}

export interface ImportedVideo {
  clip: VideoClipRef;
  /** For placing the layer immediately, before any network round trip. */
  posterBlob: Blob;
  /** Durable poster URL when the upload succeeded, else ''. */
  posterUrl: string;
  /** False when the media lives only in this browser. */
  backedUp: boolean;
}

/** Import a video file as a clip layer. Throws only for invalid or undecodable files. */
export async function uploadProjectVideo(options: { file: File; projectId: string; durationHintMs?: number }): Promise<ImportedVideo> {
  const { file, projectId, durationHintMs } = options;
  const mimeType = videoMimeOf(file);
  if (!(STORAGE_RULES.ALLOWED_VIDEO_TYPES as readonly string[]).includes(mimeType)) {
    throw new Error('Use an MP4, WebM or MOV video file.');
  }
  if (file.size > STORAGE_RULES.MAX_VIDEO_FILE_SIZE) {
    throw new Error('Video files must be 100 MB or smaller.');
  }

  const clipId = assetId('vid');
  const posterKey = `${clipId}_poster`;
  const blob = file.type === mimeType ? file : new Blob([file], { type: mimeType });
  // Cached FIRST: the editor works instantly and offline before any upload.
  await putVideoAsset(clipId, blob);

  let probe: VideoProbe;
  try {
    probe = await probeVideo(blob, durationHintMs);
  } catch (err) {
    void deleteVideoAsset(clipId);
    throw err;
  }
  await putVideoAsset(posterKey, probe.poster);

  const local = createClipRef({
    clipId,
    source: 'idb',
    mimeType,
    naturalWidth: probe.width,
    naturalHeight: probe.height,
    durationMs: probe.durationMs,
    fps: probe.fps,
    idbKey: clipId,
    posterIdbKey: posterKey,
  });
  const result: ImportedVideo = { clip: local, posterBlob: probe.poster, posterUrl: '', backedUp: false };
  const uid = getClientAuth().currentUser?.uid;
  if (!uid || !projectId) return result;

  try {
    const dir = STORAGE_PATHS.projectVideoAssets(uid, projectId);
    const storagePath = `${dir}/${clipId}_${sanitizeFilename(file.name, 'video')}`;
    const posterStoragePath = `${dir}/${posterKey}.jpg`;
    const storage = getClientStorage();
    const sourceRef = ref(storage, storagePath);
    const posterRef = ref(storage, posterStoragePath);
    await uploadBytes(sourceRef, blob, {
      contentType: mimeType,
      customMetadata: { projectId, kind: 'video', originalName: (file.name || '').slice(0, 120) },
    });
    await uploadBytes(posterRef, probe.poster, {
      contentType: 'image/jpeg',
      customMetadata: { projectId, kind: 'video-poster' },
    });
    const [url, posterUrl] = await Promise.all([getDownloadURL(sourceRef), getDownloadURL(posterRef)]);
    await setDoc(doc(getClientDB(), 'users', uid, 'projects', projectId, 'videoAssets', clipId), {
      kind: 'video',
      name: (file.name || 'Video').replace(/\.[^.]+$/, '').slice(0, 80),
      storagePath,
      posterStoragePath,
      mimeType,
      sizeBytes: blob.size,
      durationMs: probe.durationMs,
      width: probe.width,
      height: probe.height,
      createdAt: serverTimestamp(),
    });
    return {
      clip: { ...local, source: 'storage', storagePath, url, posterStoragePath },
      posterBlob: probe.poster,
      posterUrl,
      backedUp: true,
    };
  } catch (err) {
    console.warn('Video upload failed; keeping the local copy only', err);
    return result;
  }
}

/**
 * Bytes for a media ref: IndexedDB first, then Storage (re-caching into IDB
 * so the next read is local), then a plain URL. Null when nothing works —
 * callers degrade (silent export, no preview audio) rather than throw.
 */
export async function resolveMediaBlob(media: MediaRef): Promise<Blob | null> {
  if (media.idbKey) {
    try {
      const cached = await getVideoAsset(media.idbKey);
      if (cached) return cached;
    } catch {
      /* IDB unavailable (private mode); fall through to the network */
    }
  }
  let blob: Blob | null = null;
  try {
    if (media.storagePath) blob = await getBlob(ref(getClientStorage(), media.storagePath));
  } catch {
    blob = null;
  }
  if (!blob && media.url) {
    try {
      const res = await fetch(media.url, { mode: 'cors', credentials: 'omit' });
      if (res.ok) blob = await res.blob();
    } catch {
      blob = null;
    }
  }
  if (blob && blob.size > 0) {
    if (media.idbKey) void putVideoAsset(media.idbKey, blob).catch(() => undefined);
    return blob;
  }
  return null;
}

/**
 * A URL an <audio> element can play: a blob: URL from IDB when possible,
 * otherwise the Storage download URL (media elements don't need CORS to
 * play, only to be read into a canvas or WebAudio). Caller revokes blob: URLs.
 */
export async function resolvePlayableUrl(media: MediaRef): Promise<string | null> {
  if (media.idbKey) {
    try {
      const cached = await getVideoAsset(media.idbKey);
      if (cached) return URL.createObjectURL(cached);
    } catch {
      /* fall through */
    }
  }
  if (media.storagePath) {
    try {
      return await getDownloadURL(ref(getClientStorage(), media.storagePath));
    } catch {
      /* fall through */
    }
  }
  return media.url || null;
}
